// src/components/admin/batches/BatchCreationSidebar.tsx
import React from "react";
import { useBatchCreationStore } from "src/store/batchCreationStore";
import {
  User,
  Package,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Layers,
  MapPin,
  GraduationCap,
  Receipt,
} from "lucide-react";

export const BatchCreationSidebar: React.FC = () => {
  const { selectedStudentData, selectedSubscription } = useBatchCreationStore();

  const handleViewStudentRecord = (studentId: string) => {
    window.open(`/admin/users/view?id=${encodeURIComponent(studentId)}`, "_blank");
  };

  const handleViewSubscriptions = (studentId: string) => {
    window.open(`/admin/enrollments?studentId=${encodeURIComponent(studentId)}`, "_blank");
  };

  const subscription = selectedStudentData?.subscriptions.find(
    (sub) => sub.id === selectedSubscription
  );

  if (!selectedStudentData) {
    return (
      <div className="space-y-5 sticky top-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center mx-auto">
            <User className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-800">No Student Selected</h4>
            <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">
              Select an enrolled student from the configuration card to inspect their intelligence dossier and commercial ledger.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const initials = `${selectedStudentData.first_name?.[0] || ""}${
    selectedStudentData.last_name?.[0] || ""
  }`.toUpperCase() || "ST";

  const totalSessions = subscription?.sessions_count ?? 16;
  const isPaidSettled =
    subscription?.amount_due != null ? Number(subscription.amount_due) <= 0 : true;

  return (
    <div className="space-y-5 sticky top-6">
      {/* ========================================================================= */}
      {/* Card 1: Student Snapshot & Quick Links                                    */}
      {/* ========================================================================= */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 bg-[#faf9fe] border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-purple-700" />
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Student Intelligence
            </h3>
          </div>
          <button
            type="button"
            onClick={() => handleViewStudentRecord(selectedStudentData.id)}
            className="text-xs font-semibold text-purple-700 hover:text-purple-800 flex items-center gap-1"
          >
            View Record
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-start gap-3.5">
            <div className="w-11 h-11 rounded-full bg-purple-100 text-purple-800 font-bold flex items-center justify-center text-sm shrink-0 border border-purple-200">
              {initials}
            </div>
            <div className="space-y-0.5">
              <h4 className="text-sm font-bold text-slate-900">
                {selectedStudentData.first_name} {selectedStudentData.last_name}
              </h4>
              <p className="text-xs text-slate-500 font-mono">
                {selectedStudentData.email}
              </p>
              <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
                {selectedStudentData.board && (
                  <span className="px-2 py-0.5 rounded font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                    {selectedStudentData.board}
                  </span>
                )}
                {selectedStudentData.education_level && (
                  <span className="px-2 py-0.5 rounded font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                    {selectedStudentData.education_level}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Student Support Flag / Admin Assistance Alert */}
          {subscription?.admin_assistance_required ? (
            <div className="p-3 bg-amber-50/90 border border-amber-200 rounded-lg space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-900">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                  Student Support Flag
                </span>
                <span className="px-1.5 py-0.2 bg-amber-200 text-amber-900 rounded text-[11px] font-bold">
                  Assistance Req
                </span>
              </div>
              <p className="text-xs text-amber-800 leading-relaxed italic">
                "{subscription.admin_assistance_notes || "Student requested personalized tutor matching."}"
              </p>
            </div>
          ) : (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                No special academic assistance flags
              </span>
              <button
                type="button"
                onClick={() => handleViewSubscriptions(selectedStudentData.id)}
                className="text-purple-700 hover:underline font-medium text-[11px]"
              >
                All Enrolments →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* Card 2: Commercial & Quota Ledger                                         */}
      {/* ========================================================================= */}
      {subscription ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 bg-[#faf9fe] border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-purple-700" />
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                Commercial Ledger & Quota
              </h3>
            </div>
            <span
              className={`px-2 py-0.5 rounded text-xs font-bold border ${
                isPaidSettled
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }`}
            >
              {isPaidSettled ? "Payment Settled" : "Payment Pending"}
            </span>
          </div>

          <div className="p-5 space-y-4">
            {/* Quota Bar */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-slate-600">Total Provisioned Sessions</span>
                <span className="font-bold text-slate-900 font-mono">
                  {totalSessions} / {totalSessions} Available
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                <div className="bg-purple-600 h-2 rounded-full w-full"></div>
              </div>
              <div className="flex justify-between text-xs text-slate-500 font-mono">
                <span>0 Consumed</span>
                <span>{totalSessions} Unscheduled</span>
              </div>
            </div>

            {/* Compact Financial Key-Value Grid */}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100 text-xs">
              <div>
                <span className="text-slate-500 block">Package Price</span>
                <span className="font-bold text-slate-900 font-mono text-sm">
                  {formatMoney(
                    subscription.actual_purchase_price ?? subscription.total_purchase_price,
                    subscription.currency
                  )}
                </span>
              </div>
              <div>
                <span className="text-slate-500 block">Rate / Session</span>
                <span className="font-bold text-slate-900 font-mono text-sm">
                  {formatMoney(
                    subscription.actual_price_per_session ?? subscription.default_price_per_session,
                    subscription.currency
                  )}
                </span>
              </div>
              <div>
                <span className="text-slate-500 block">Total Received</span>
                <span className="font-semibold text-emerald-700 font-mono">
                  {formatMoney(subscription.total_amount_received, subscription.currency)}
                </span>
              </div>
              <div>
                <span className="text-slate-500 block">Outstanding Balance</span>
                <span
                  className={`font-semibold font-mono ${
                    Number(subscription.amount_due) > 0 ? "text-rose-600" : "text-slate-900"
                  }`}
                >
                  {formatMoney(subscription.amount_due ?? 0, subscription.currency)}
                </span>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
              <span className="text-slate-500">
                SKU: <code className="font-mono text-slate-700">{subscription.product_id?.slice(0, 8) || "PKG-ENR"}</code>
              </span>
              <button
                type="button"
                onClick={() => handleViewSubscriptions(selectedStudentData.id)}
                className="font-semibold text-purple-700 hover:text-purple-800"
              >
                Open Billing Records →
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 text-center text-xs text-slate-500">
          Select an active subscription to inspect quota and commercial balances.
        </div>
      )}
    </div>
  );
};

const formatMoney = (amount?: number | null, currency?: string | null) => {
  if (amount == null) return "USD 0.00";
  return `${(currency || "usd").toUpperCase()} ${Number(amount).toFixed(2)}`;
};

export default BatchCreationSidebar;
